import { ContainerClient } from '@azure/storage-blob';
import { CONSTANT } from '@const';
import {
	FileUploadAPIError,
	FileUploadLinkResponse,
	FileUploadStatus,
	FileUploadStatusResponse,
} from '@types';

import { ApiParams, ApiService } from '../api/ApiService';

export class FileUploadService extends ApiService {
	protected static baseUrl = CONSTANT.API_BASE_URL;

	/**
	 * Retrieves a file upload link from the server.
	 * @returns {Promise<FileUploadLinkResponse>} A promise that resolves to the file upload link response.
	 */
	private static async getFileUploadLink(
		abortSignal?: AbortSignal,
	): Promise<FileUploadLinkResponse> {
		const params: ApiParams = {
			method: 'GET',
			url: `/fileuploadlink`,
			abortSignal,
		};
		return await this.executeApiRequest(params);
	}

	/**
	 * Retrieves the status of a file upload.
	 * @param {string} id - The ID of the file upload.
	 * @returns {Promise<FileUploadStatusResponse>} A promise that resolves to the file upload status response.
	 */
	private static async getFileUploadStatus(
		id: string,
		abortSignal?: AbortSignal,
	): Promise<FileUploadStatusResponse> {
		const params: ApiParams = {
			method: 'POST',
			url: `/fileuploadstatus`,
			data: { id: id },
			abortSignal,
		};
		return await this.executeApiRequest(params);
	}

	/**
	 * Uploads a file to Azure Blob Storage.
	 * @param {File} file - The file to upload.
	 * @param {string} id - The ID to use for the file name.
	 * @param {string} url - The SAS URL for the blob container.
	 * @param {AbortSignal} [abortSignal] - An optional AbortSignal to cancel the upload.
	 * @returns {Promise<void>} A promise that resolves when the upload is complete.
	 */
	private static async uploadFileToBlob(
		file: File,
		id: string,
		url: string,
		abortSignal?: AbortSignal,
	): Promise<void> {
		// Rename the file using guid before upload
		const fileExtension = file.name.split('.').pop();
		const newFileName = `${id}.${fileExtension}`;

		// Create ContainerClient from the URL
		const containerClient = new ContainerClient(url);

		// Get a block blob client
		const blockBlobClient = containerClient.getBlockBlobClient(newFileName);

		// Upload the blob
		await blockBlobClient.uploadData(file, {
			blobHTTPHeaders: { blobContentType: file.type },
			abortSignal: abortSignal,
		});
	}

	/**
	 * Uploads a file and checks its status until completion or timeout.
	 * @param {File} file - The file to upload.
	 * @param {AbortSignal} [abortSignal] - An optional AbortSignal to cancel the upload.
	 * @param {number} [timeout=120000] - The maximum time to wait for the upload to complete, in milliseconds, defaults to 120 seconds.
	 * @param {number} [uploadStatusTimeout=120000] - The maximum time to wait for the upload status to be checked, in milliseconds, defaults to 120 seconds.
	 * @returns {Promise<FileUploadStatusResponse>} A promise that resolves to the final upload status.
	 * @throws {Error} If the upload times out, encounters an error, or if the status is Dirty or Failed.
	 */
	static async uploadFile(
		file: File,
		abortSignal?: AbortSignal,
		timeout = 120000, // previous value: 60000
		uploadStatusTimeout = 120000, // previous value: 45000
	): Promise<FileUploadStatusResponse> {
		const UPLOAD_CANCELLED = 'Upload cancelled';
		const cancellationPromise = (): Promise<never> => {
			return new Promise((_, reject) => {
				if (abortSignal) {
					abortSignal.addEventListener('abort', () => reject(new Error(UPLOAD_CANCELLED)));
				}
			});
		};

		const timeoutPromise = async () => {
			await new Promise(resolve => setTimeout(resolve, timeout));
			throw new Error(FileUploadAPIError.CONNECTION_TIMEOUT);
		};

		const uploadPromise = async () => {
			// Get the upload link and ID
			const { sasuri, id } = await FileUploadService.getFileUploadLink(abortSignal);

			await FileUploadService.uploadFileToBlob(file, id, sasuri, abortSignal);

			// Check upload status
			const statusCheckStartTime = Date.now();
			let uploadStatus: FileUploadStatusResponse;

			do {
				// Call status endpoint every 5 seconds
				await new Promise(resolve => setTimeout(resolve, 5000));
				uploadStatus = await FileUploadService.getFileUploadStatus(id, abortSignal);

				// Check if the status check has exceeded the uploadStatusTimeout
				if (Date.now() - statusCheckStartTime > uploadStatusTimeout) {
					throw new Error(FileUploadAPIError.CONNECTION_TIMEOUT);
				}
			} while (uploadStatus.status === FileUploadStatus.REQUESTED);

			if (uploadStatus.status === FileUploadStatus.DIRTY) {
				throw new Error(FileUploadAPIError.MALWARE_DETECTED);
			}
			if (uploadStatus.status === FileUploadStatus.FAILED) {
				throw new Error(FileUploadAPIError.UPLOAD_FAILED);
			}

			return uploadStatus;
		};

		try {
			return await Promise.race([uploadPromise(), timeoutPromise(), cancellationPromise()]);
		} catch (error) {
			if (error instanceof Error && error.message === UPLOAD_CANCELLED) {
				console.log('File upload cancelled', file.name);
			}
			throw error;
		}
	}
}
